import type { FleetOverviewParams } from '../types/fleetOverview';

export const queryKeys = {
  getFleetOverview: (params: FleetOverviewParams) => ['fleet-overview', params],
  featureFlags: ['feature-flags'],
  getFeatureFlags: (userId: number | undefined) => ['feature-flags', 'list', userId],
  getEffectiveFeatureFlags: (userId: number | undefined) => ['feature-flags', 'effective', userId],
  getFeatureFlagUsers: (key: string | undefined, userId: number | undefined) => [
    'feature-flags',
    'users',
    key,
    userId,
  ],
  getRuntimeConfig: ['runtime-config'],
  getAddresses: (search: string) => ['addresses-search', { search }],
  getAdminSessions: (first: number, max: number, dedupeUsers: boolean) => [
    'admin-sessions',
    { first, max, dedupeUsers: dedupeUsers.toString() },
  ],
  getProjectStageKpi: (from: Date, to: Date) => [
    'project-stage-kpi',
    { from: from.toISOString(), to: to.toISOString() },
  ],
  getAdminFleetStatus: () => ['admin-fleet-status'],
  getAdminProjectWeeklySummaryKpi: () => ['admin-project-weekly-summary-kpi'],
  getDevices: (search: string) => ['devices', { search }],
  getDistributors: ['distributors'],
  getCompanies: (status?: string, includeProjects?: boolean, includeUsers?: boolean) => [
    'companies',
    { status: status ?? 'status', includeProjects, includeUsers },
  ],
  getProjectContextImg: (projectId: string) => ['project-context-img', { projectId }],
  getProjectOrientationImg: (projectId: string) => ['project-orientation-img', { projectId }],
  getDocumentationDocs: (prefix?: string) => ['documentation-docs', { prefix: prefix ?? 'prefix' }],
  getProjects: (
    includeCompany?: boolean,
    includeTeam?: boolean,
    includeGatewayStatus?: boolean,
    includeSensorStatus?: boolean,
    stages?: string[]
  ) => [
    'projects',
    {
      includeCompany: includeCompany?.toString(),
      includeTeam: includeTeam?.toString(),
      includeGatewayStatus: includeGatewayStatus?.toString(),
      includeSensorStatus: includeSensorStatus?.toString(),
      stages: stages?.join(','),
    },
  ],
  getAsBuiltDocs: (projectId: string) => ['as-built-docs', { projectId }],
  getProjectUsers: (projectId: string) => ['project-users', { projectId }],
  getProjectTeamInvites: (projectId: string) => ['project-team-invites', { projectId }],
  getUser: (userId: string) => [`user/${userId}`],
  getProfilePicture: () => ['profile-picture'],
  getUserNotifications: () => ['user-notifications'],
  getSensors: (
    projectId: string,
    installationId: string,
    status: string,
    includeReplaced?: boolean
  ) => [
    'sensors',
    { projectId, installationId, status, includeReplaced: includeReplaced?.toString() },
  ],
  getGateways: (projectId: string, installationId: string) => [
    'gateways',
    { projectId, installationId },
  ],
  getInstallationTasks: (projectId: string, installationId: string) => [
    'installation-tasks',
    { projectId, installationId },
  ],
  getVentCaps: (projectId: string, installationId?: string) => [
    'ventcaps',
    { projectId, installationId: installationId ?? 'installationId' },
  ],
  getInstallationPhoto: (projectId: string, documentationFileId: number) => [
    'installation-photo',
    { projectId, documentationFileId },
  ],
  getDocumentationFiles: (projectId: string) => ['documentation-files', { projectId }],
  getUserExists: (email?: string, projectId?: string) => ['user-exists', { email, projectId }],
  getUserProjectRole: (projectId: string) => ['user-project-role', { projectId }],
  getSensorDataSingle: (projectId: string, sensorId: number) => [
    'sensorDataSingle',
    { projectId, sensorId },
  ],
  getSensorDataLatest: (projectId: string, installationId?: number) => [
    'sensorDataLatest',
    { projectId, installationId },
  ],
  getSensorData: (projectId: string, from: string, to: string, sensorIds?: string) => [
    'sensorData',
    { projectId, from, to, sensorIds },
  ],
  getSensorDataAggregated: (
    projectId: string,
    from: string,
    to: string,
    includeStatistics = false,
    sensorIds?: string
  ) => ['sensorDataAggregated', { projectId, from, to, includeStatistics, sensorIds }],
  getReports: (projectId: string | number) => ['reports', { projectId: projectId.toString() }],
  getInstallations: (projectId: string | number) => [
    'installations',
    { projectId: projectId.toString() },
  ],
  getInstallationPlans: (projectId: string | number) => [
    'installation-plans',
    { projectId: projectId.toString() },
  ],
  getInstallation: (projectId: string | number, installationId: string | number) => [
    'installations',
    {
      projectId: projectId.toString(),
      installationId: installationId.toString(),
    },
  ],
  getInstallationMap: (projectId: string | number, installationId: string | number) => [
    'installation-map',
    {
      projectId: projectId.toString(),
      installationId: installationId.toString(),
    },
  ],
  getWeatherData: (projectId: string | number, from: string, to: string) => [
    'weatherData',
    { projectId: projectId.toString(), from, to },
  ],
  getProject: (
    projectId: string,
    includeCompany?: boolean,
    includeTeam?: boolean,
    includeGatewayStatus?: boolean,
    includeSensorStatus?: boolean,
    sensorStatusReferenceDate?: Date
  ) => [
    'project',
    {
      projectId,
      includeCompany: includeCompany?.toString(),
      includeTeam: includeTeam?.toString(),
      includeGatewayStatus: includeGatewayStatus?.toString(),
      includeSensorStatus: includeSensorStatus?.toString(),
      sensorStatusReferenceDate: sensorStatusReferenceDate?.toISOString(),
    },
  ],
  getProjectDocumentation: (projectId: string) => ['project-documentation', { projectId }],
  getUsers: (includeProjects: boolean) => [
    `users`,
    { includeProjects: includeProjects?.toString() },
  ],
  getInvites: ['invites'],
  getCredentials: ['credentials'],
  getSessions: ['sessions'],
  getWeeklySummaries: (projectId: string, limit?: number) => [
    'weekly-summaries',
    { projectId, limit },
  ],
} as const;
